import * as React from 'react'
import { useState } from 'react'
import toast from 'react-hot-toast'
import { Box, Button } from '@mui/material'
import config from '../../../config'
import { useUser } from '../../../hooks/useUser'
import { useWorkspaceBrandId } from '../../../hooks/useWorkspaceBrandId'

// The site-wide tag (gt.js on shoppers.thedesirecompany.com). One install
// per site: it finds the brand's videos on product pages and records plays
// and watch time there, plus cart / checkout / order-confirmation events.
// Two shapes of the same install: a plain <head> tag, and the two-line
// form Google Tag Manager needs (GTM re-creates the script element, so the
// brand id travels on window.tdcGTag instead of an attribute).

const CODE_SX = {
  backgroundColor: '#333',
  color: '#ffffff',
  fontWeight: 300,
  lineHeight: '18px',
  fontSize: '12px',
  marginBottom: '8px',
  marginTop: '12px',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
}

export function Snippet({ label, code }) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code)
      setCopied(true)
      toast.success('Copied to clipboard')
      setTimeout(() => setCopied(false), 2000)
    } catch (e) {
      toast.error('Could not copy — select the code and copy it manually')
    }
  }
  return (
    <Box marginBottom="28px">
      <strong>{label}</strong>
      <Box padding={2} borderRadius={1.25} sx={CODE_SX}>
        <code>{code}</code>
      </Box>
      <Button size="small" variant="outlined" onClick={copy}>
        {copied ? 'Copied!' : 'Copy code'}
      </Button>
    </Box>
  )
}

// `brandId` prop: the internal brand detail page renders the tag for the
// brand it is showing; brand users get theirs from the workspace / membership.
export default function GoogleTag({ brandId: brandIdProp = 0 } = {}) {
  const { brand } = useUser()
  const workspaceBrandId = useWorkspaceBrandId()
  const brandId = brandIdProp || workspaceBrandId || (brand && brand[0] && brand[0].id)
  const src = `${config.EMBED_HOST}/assets/js/gt.js`

  const headCode = `<script id="tdcGTv1" data-bid="${brandId}" src="${src}" async></script>`
  const gtmCode = `<script>window.tdcGTag = { bid: ${brandId} };</script>\n<script src="${src}" async></script>`

  return (
    <>
      <p>
        Install the tag once on your website to track your videos on your product pages — plays and watch
        time wherever one of your videos is found — plus add-to-cart, checkout and order-confirmation events.
        The tag needs no other setup: it recognizes your videos automatically.
      </p>
      <Snippet
        label={<>Option A — paste into the <code>{'<head>'}</code> of every page</>}
        code={headCode}
      />
      <Snippet
        label="Option B — Google Tag Manager: add a Custom HTML tag firing on All Pages with this content"
        code={gtmCode}
      />
      <p style={{ fontSize: '13px', opacity: 0.8 }}>
        Loading the tag twice is harmless. If your site sets a strict Content-Security-Policy, allow
        <code> {config.EMBED_HOST}</code> for scripts and our API for connections, or the tag cannot report.
      </p>
    </>
  )
}
